// ContactList.js
import React, { useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Chip,
  Paper,
  Stack,
  Select,
  MenuItem,
  useTheme,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Visibility,
  Phone,
  Email,
  Person,
  Business,
  Refresh,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";
import {
  CONTACT_TYPES,
  CONTACT_TYPE_LABELS,
  COMPANY_ONLY_TYPES,
} from "../../utils/models/contactModel";

const ContactList = ({ onEdit, onView }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: contacts, loading } = useCollection("contacts");
  const [localContacts, setLocalContacts] = useState(null);
  const { remove } = useAuditFirestore("contacts");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contactTypeFilter, setContactTypeFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  React.useEffect(() => {
    if (contacts) {
      setLocalContacts(contacts);
    }
  }, [contacts]);

  React.useEffect(() => {
    setPage(0);
  }, [search, contactTypeFilter]);

  const filtered = localContacts?.filter(
    (c) =>
      c.isActive !== false &&
      (contactTypeFilter === "" || c.contactType === contactTypeFilter) &&
      (search === "" ||
        `${c.firstName || ""} ${c.lastName || ""} ${c.companyName || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.phone?.toLowerCase().includes(search.toLowerCase())),
  );

  const paginatedContacts = filtered?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDisplayName = (contact) => {
    if (COMPANY_ONLY_TYPES.includes(contact.contactType)) {
      return contact.companyName || "Sin nombre";
    }
    if (
      contact.contactType === CONTACT_TYPES.CLIENT &&
      contact.clientType === "company"
    ) {
      return contact.companyName || "Sin nombre";
    }
    return (
      `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
      "Sin nombre"
    );
  };

  const getInitials = (contact) => {
    if (COMPANY_ONLY_TYPES.includes(contact.contactType)) {
      return (contact.companyName || "??").substring(0, 2).toUpperCase();
    }
    if (
      contact.contactType === CONTACT_TYPES.CLIENT &&
      contact.clientType === "company"
    ) {
      return (contact.companyName || "??").substring(0, 2).toUpperCase();
    }
    return `${contact.firstName?.[0] || ""}${contact.lastName?.[0] || ""}`.toUpperCase();
  };

  const getSubtitle = (contact) => {
    if (
      contact.contactType === CONTACT_TYPES.CLIENT &&
      contact.clientType === "company" &&
      contact.representativeName
    ) {
      return contact.representativeName;
    }
    return null;
  };

  const getChipColor = (type) => {
    switch (type) {
      case CONTACT_TYPES.CLIENT:
        return "primary";
      case CONTACT_TYPES.SUPPLIER:
        return "warning";
      case CONTACT_TYPES.EPC:
        return "success";
      case CONTACT_TYPES.COMMERCIAL:
        return "info";
      default:
        return "default";
    }
  };

  const getChipIcon = (type) => {
    if (COMPANY_ONLY_TYPES.includes(type)) {
      return <Business fontSize="small" />;
    }
    return <Person fontSize="small" />;
  };

  const getAvatarColor = (type) => {
    switch (type) {
      case CONTACT_TYPES.CLIENT:
        return theme.palette.primary.main;
      case CONTACT_TYPES.SUPPLIER:
        return theme.palette.warning.main;
      case CONTACT_TYPES.EPC:
        return theme.palette.success.main;
      case CONTACT_TYPES.COMMERCIAL:
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };

  if (loading || !localContacts) return <LoadingSpinner />;

  return (
    <Box>
      {/* Barra de búsqueda */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0, 95, 101, 0.12)",
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          flexWrap="wrap"
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flex={1} flexWrap="wrap">
            <TextField
              placeholder="Buscar por nombre o email"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 280, md: 360 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  height: 44,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      ✖
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Select
              size="small"
              value={contactTypeFilter}
              onChange={(e) => setContactTypeFilter(e.target.value)}
              displayEmpty
              sx={{
                width: { xs: "100%", sm: 180 },
                height: 44,
                borderRadius: "12px",
                background: theme.palette.background.paper,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider,
                },
              }}
            >
              <MenuItem value="">Todos los tipos</MenuItem>
              {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Total: ${filtered?.length || 0}`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                px: 1,
                background: theme.palette.background.paper,
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />

            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => {
                  if (contacts) setLocalContacts(contacts);
                }}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabla o EmptyState */}
      {filtered?.length === 0 ? (
        <EmptyState
          title={search ? "Sin resultados" : "Sin contactos"}
          description={
            search
              ? "Prueba con otros términos"
              : "Añade contactos manualmente o importa desde CSV"
          }
          actionLabel="Nuevo Contacto"
          onAction={() => onEdit(null)}
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: { xs: 50, sm: 60 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    {isMobile ? "" : "Avatar"}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Nombre
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Teléfono
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Tipo
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: { xs: 100, sm: 130 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedContacts?.map((contact) => {
                  const subtitle = getSubtitle(contact);

                  return (
                    <TableRow
                      key={contact.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "rgba(0, 95, 101, 0.02)",
                        },
                      }}
                    >
                      <TableCell
                        onClick={() =>
                          onView ? onView(contact) : onEdit(contact)
                        }
                      >
                        <Avatar
                          sx={{
                            width: { xs: 32, sm: 40 },
                            height: { xs: 32, sm: 40 },
                            bgcolor: getAvatarColor(contact.contactType),
                            fontSize: { xs: "0.8rem", sm: "1rem" },
                            fontWeight: 500,
                            background: `linear-gradient(135deg, ${getAvatarColor(contact.contactType)} 0%, ${getAvatarColor(contact.contactType)}dd 100%)`,
                          }}
                        >
                          {getInitials(contact)}
                        </Avatar>
                      </TableCell>
                      <TableCell
                        onClick={() =>
                          onView ? onView(contact) : onEdit(contact)
                        }
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                          {getDisplayName(contact)}
                        </Typography>
                        {isMobile && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            {contact.email || contact.phone || "—"}
                          </Typography>
                        )}
                        {subtitle && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                            Rep: {subtitle}
                          </Typography>
                        )}
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell
                            onClick={() =>
                              onView ? onView(contact) : onEdit(contact)
                            }
                          >
                            {contact.email ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Email sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                  {contact.email}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              onView ? onView(contact) : onEdit(contact)
                            }
                          >
                            {contact.phone || contact.mobile ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Phone sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                  {contact.phone || contact.mobile}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </>
                      )}
                      <TableCell
                        onClick={() =>
                          onView ? onView(contact) : onEdit(contact)
                        }
                      >
                        <Chip
                          size="small"
                          icon={getChipIcon(contact.contactType)}
                          label={
                            CONTACT_TYPE_LABELS[contact.contactType] ||
                            contact.contactType
                          }
                          color={getChipColor(contact.contactType)}
                          variant="outlined"
                          sx={{
                            minWidth: { xs: 70, sm: 85 },
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            "& .MuiChip-icon": { fontSize: { xs: 12, sm: 14 }, ml: 0.5 },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          {onView && (
                            <Tooltip title="Ver detalle">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView(contact);
                                }}
                                sx={{ color: theme.palette.info.main }}
                              >
                                <Visibility sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(contact);
                              }}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          <TablePagination
            component="div"
            count={filtered?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[7, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: { xs: "0.7rem", sm: "0.85rem" },
              },
            }}
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalContacts((prev) =>
            prev.filter((c) => c.id !== deleteTarget.id),
          );
          const deletedContact = deleteTarget;
          setDeleteTarget(null);

          try {
            await remove(deletedContact.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalContacts((prev) => [...prev, deletedContact]);
          }
        }}
        title="Eliminar Contacto"
        message={`¿Eliminar a ${deleteTarget ? getDisplayName(deleteTarget) : ""}?`}
        type="delete"
      />
    </Box>
  );
};

export default ContactList;